'use client';

import TaboolaLoader, { TaboolaPageType } from './TaboolaLoader';
import TaboolaWidget from './TaboolaWidget';

interface PlacementConfig {
  mode: string;
  container: string;
  placement: string;
}

// Body-code placements from the Taboola onboarding snippet, grouped by page
// type. Homepage declares {homepage:'auto'} + 2 widgets; category declares
// {category:'auto'} + 3 widgets. Container ids / modes / placement names are
// copied verbatim from taboola.txt and must match Backstage exactly.
const PLACEMENTS: Record<'homepage' | 'category', PlacementConfig[]> = {
  homepage: [
    { mode: 'alternating-thumbnails-a', container: 'taboola-below-homepage-thumbnails', placement: 'Below Homepage Thumbnails' },
  ],
  category: [
    { mode: 'alternating-thumbnails-a', container: 'taboola-below-category-thumbnails', placement: 'Below Category Thumbnails' },
  ],
};

/**
 * Drops the Taboola head code (loader + page-type declaration) and the page's
 * widget placements onto a homepage or category page. Rendered once per page by
 * BuilderPage, which serves both `/` (`homepage`) and section pages (`category`)
 * via the middleware `/builder/:slug` rewrite.
 *
 * Placements render stacked in the main content column. Vertical position can be
 * refined later by moving individual <TaboolaWidget>s into specific page-builder
 * sections; the container ids/modes are what Backstage keys on, not where they sit.
 */
export default function TaboolaPageWidgets({
  pageType,
  pageKey,
}: {
  pageType: Extract<TaboolaPageType, 'homepage' | 'category'>;
  /** Section slug (or any per-page value) — see TaboolaWidget's pageKey doc comment. */
  pageKey?: string;
}) {
  return (
    <div className="taboola-page-widgets mx-auto w-full max-w-[1245px] px-2">
      <TaboolaLoader pageType={pageType} pageKey={pageKey} />
      {PLACEMENTS[pageType].map((p) => (
        <TaboolaWidget
          key={p.container}
          mode={p.mode}
          container={p.container}
          placement={p.placement}
          pageKey={pageKey}
          className="my-4"
        />
      ))}
    </div>
  );
}
